import { IContext } from "@src/context";
import { vhmanalytics } from "@src/proto/jsonapi";
import { getPageCategory } from "@src/utils/helper";
import { sha256Hash } from "@src/utils/hash";
import { randomUUID } from "@src/utils/uuid";

import {
  IEcommerce,
  IEcommerceItem,
  IEnquiryConversion,
  IEnquiryForm,
  ICorporateEnquiryForm,
} from "./types";
import {
  pageViewEvent,
  newPhoneButtonClickEvent,
  parkingPhoneNumberButtonClickEvent,
  newEnquiryFormButtonClickEvent,
  newAppointmentButtonClickEvent,
  newEnquiryFormSubmissionEvent,
  parkingEnquiryFormSubmissionEvent,
  newAppointmentSubmissionEvent,
} from "./events";
import {
  IReturn,
  fetchAnalyticData,
  getAnalyticDataFromBreadcrumb,
} from "./getAnalyticData";

const CURRENCY = "JPY";
const PAGE_CATEGORY = getPageCategory();

export const triggerPageViewEvent = (context: IContext) => {
  const { locale } = context;
  const path = window.location.pathname.replace(
    new RegExp(`^/${locale}/`),
    "/"
  ); // remove locale prefix from path
  const pathParts = path.split("/").filter((part) => part !== "");

  pageViewEvent({
    language: locale,
    pageCategory:
      path === "/"
        ? "home"
        : pathParts[0] === "rent" ||
          pathParts[0] === "chintai" ||
          pathParts[0] === "thue" ||
          pathParts[0] === "apartamentos" ||
          pathParts[0] === "zufang" ||
          pathParts[0] === "einnghar" ||
          pathParts[0] === "sewa" ||
          pathParts[0] === "bhada"
        ? pathParts.length < 5
          ? "category"
          : "detail"
        : path.startsWith("/chushajo/chintai/") ||
          path.startsWith("/parking/rent/") ||
          path.startsWith("/estacionamento/alugar/") ||
          path.startsWith("/bai-do-xe/thue/") ||
          path.startsWith("/parking/bhada/") ||
          path.startsWith("/parkir/sewa/") ||
          path.startsWith("/parking/thone/")
        ? pathParts.length < 6
          ? "category"
          : "detail"
        : path === "/campaign/"
        ? "campaign"
        : path === "/faqs/"
        ? "faq"
        : path === "/short-list/"
        ? "short-list"
        : path === "/search-results/" || path === "/parking-search-results/"
        ? "search"
        : path === "/thank-you/"
        ? "conversion"
        : "other",
  });
};

export const getEcommerceItemsByIds = async (
  ids: string[],
  category?: string
): Promise<IEcommerceItem[]> => {
  try {
    const isParking = (category || PAGE_CATEGORY) === "parking";
    const analytics: vhmanalytics.IAnalytics = await fetchAnalyticData();
    return ids.map((id, index) => {
      const property = analytics.properties?.[id];
      const prefecture =
        analytics.prefectures?.[property?.pid ?? ""]?.name || "";
      const city = analytics.cities?.[property?.cid ?? ""]?.pCid
        ? analytics.cities?.[
            analytics.cities?.[property?.cid ?? ""]?.pCid ?? ""
          ]?.name || ""
        : analytics.cities?.[property?.cid ?? ""]?.name || "";

      return {
        item_id: "PLA-FTVH-1-" + id,
        item_name: (property?.name || "") + (isParking ? "駐車場" : ""),
        item_category: prefecture,
        item_category2: city,
        item_category3: property?.pstl || "",
        item_category4: prefecture + city + property?.addr,
        item_variant: isParking
          ? property?.pkRent
            ? "Y"
            : "N"
          : property?.rent
          ? "Y"
          : "N",
        price: (isParking ? property?.pkRent : property?.rent) ?? 0,
        quantity: 1,
        index: index + 1,
      };
    });
  } catch (error) {
    return [];
  }
};

const getCityNamesByIds = async (ids: string[]): Promise<string[]> => {
  try {
    const analytics: vhmanalytics.IAnalytics = await fetchAnalyticData();
    return ids.map((id) => {
      return analytics.cities?.[id]?.name || id;
    });
  } catch (error) {
    return ids;
  }
};

export const getEcommerceByItems = (items: IEcommerceItem[]): IEcommerce => {
  return {
    currency: CURRENCY,
    value: items.reduce((sum, item) => sum + item.price, 0),
    items,
  };
};

export const getEcommerceByIds = async (
  ids: string[],
  category?: string
): Promise<IEcommerce> => {
  const items = await getEcommerceItemsByIds(ids, category);
  return getEcommerceByItems(items);
};

export const getItemListNameFromBreadcrumb = async (): Promise<string> => {
  const breadcrumbs = (await getAnalyticDataFromBreadcrumb()) as IReturn;
  return [
    breadcrumbs?.regions?.name,
    breadcrumbs?.prefectures?.name,
    breadcrumbs?.cities?.name,
    breadcrumbs?.lines?.name,
    breadcrumbs?.stations?.name,
  ]
    .filter((item) => !!item)
    .join("|");
};

export const getPropertyIdFromPage = (): string | undefined => {
  // PDP page
  const showcase = document.querySelector(".container-showcase");
  return showcase?.getAttribute("data-property-id") || undefined;
};

export const generateConversionId = (): string => {
  // The unique identifier for the conversion event, instrumented as a 21-digit unix timestamp + random number numeric string: {{ UNIX timestamp (in millisec) }} + {{ 8-digit random number }}
  const now = new Date();
  const timestamp = now.getTime();
  const random = Math.floor(Math.random() * 100000000);
  return `${timestamp}${random}`;
};

export const generateEventId = (): string => {
  return randomUUID();
};

export const generateRecordId = (): string => {
  let recordId = sessionStorage.getItem("vhmRecordId");
  if (!recordId) {
    recordId = randomUUID();
    sessionStorage.setItem("vhmRecordId", recordId);
  }
  return recordId;
};

export const triggerNewPhoneButtonClickEvent = (data: {
  propertyId?: string;
  enquiryType: string;
  recordId: string;
}) => {
  const enquiry: IEnquiryConversion = {
    conversion_id: generateConversionId(),
    record_id: data.recordId,
    enquiry_type: data.enquiryType,
  };
  const pushEvent =
    PAGE_CATEGORY === "parking"
      ? parkingPhoneNumberButtonClickEvent
      : newPhoneButtonClickEvent;
  if (data.propertyId) {
    getEcommerceItemsByIds([data.propertyId]).then((items) => {
      // remove `index` from item[0]
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { index, ...item } = items[0];
      pushEvent({
        ...item,
        ...enquiry,
      });
    });
  } else {
    pushEvent(enquiry);
  }
};

export const triggerNewEnquiryFormButtonClickEvent = (data: {
  propertyId?: string;
  enquiryType: string;
}) => {
  if (data.propertyId) {
    getEcommerceItemsByIds([data.propertyId]).then((items) => {
      // remove `index` from item[0]
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { index, ...item } = items[0];
      newEnquiryFormButtonClickEvent({
        ...item,
        enquiry_type: data.enquiryType,
      });
    });
  } else {
    newEnquiryFormButtonClickEvent({ enquiry_type: data.enquiryType });
  }
};

export const triggerNewAppointmentButtonClickEvent = (data: {
  propertyId: string;
}) => {
  getEcommerceItemsByIds([data.propertyId]).then((items) => {
    // remove `index` from item[0]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { index, ...item } = items[0];
    newAppointmentButtonClickEvent({
      ...item,
      enquiry_type: "visit_appointment",
    });
  });
};

interface INewEnquiryFormSubmissionEvent {
  propertyId?: string;
  name: string;
  email: string;
  phone: string;
  recordId: string;
  // The following is the corporate enquiry
  selectedCity?: string[];
  numberOfResidents?: string; // input value is string
  budgetPerPerson?: string;
}

interface IParkingEnquiryFormSubmissionEvent {
  propertyId?: string;
  name: string;
  email: string;
  phone: string;
  recordId: string;
}

interface INewAppointmentSubmissionEvent {
  propertyId: string;
  name: string;
  email: string;
  phone: string;
  recordId: string;
}

export const triggerNewEnquiryFormSubmissionEvent = async (
  data: INewEnquiryFormSubmissionEvent
) => {
  const enquiry: IEnquiryForm = {
    conversion_id: generateConversionId(),
    record_id: data.recordId,
    enquiry_type: "general_enquiry",
    first_name: sha256Hash(data.name),
    last_name: sha256Hash(data.name),
    hashed_email: sha256Hash(data.email),
    hashed_phone: sha256Hash(data.phone),
  };
  if (data.propertyId) {
    getEcommerceItemsByIds([data.propertyId]).then((items) => {
      // remove `index` from item[0]
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { index, ...item } = items[0];
      newEnquiryFormSubmissionEvent({
        ...item,
        ...enquiry,
        enquiry_type: "property_enquiry",
      });
    });
  } else if (data.selectedCity) {
    // corporate enquiry form case
    // remove `first_name` and `last_name` from enquiry
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { first_name, last_name, ...corporateEnquiry } = enquiry;
    const submission: ICorporateEnquiryForm = {
      ...corporateEnquiry,
      enquiry_type: "corporate_housing_enquiry",
      selected_city: await getCityNamesByIds(data.selectedCity),
      company_name: data.name,
    };
    if (data.numberOfResidents) {
      submission.number_of_residents = Number(data.numberOfResidents);
    }
    if (data.budgetPerPerson) {
      submission.budget_per_person = data.budgetPerPerson;
    }
    newEnquiryFormSubmissionEvent(submission);
  } else {
    newEnquiryFormSubmissionEvent(enquiry);
  }
};

export const triggerParkingEnquiryFormSubmissionEvent = async (
  data: IParkingEnquiryFormSubmissionEvent
) => {
  const enquiry: IEnquiryForm = {
    conversion_id: generateConversionId(),
    record_id: data.recordId,
    enquiry_type: "general_enquiry",
    first_name: sha256Hash(data.name),
    last_name: sha256Hash(data.name),
    hashed_email: sha256Hash(data.email),
    hashed_phone: sha256Hash(data.phone),
  };
  if (data.propertyId) {
    getEcommerceItemsByIds([data.propertyId], "parking").then((items) => {
      // remove `index` from item[0]
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { index, ...item } = items[0];
      parkingEnquiryFormSubmissionEvent({
        ...item,
        ...enquiry,
        enquiry_type: "parking_enquiry",
      });
    });
  } else {
    parkingEnquiryFormSubmissionEvent(enquiry);
  }
};

export const triggerNewAppointmentSubmissionEvent = (
  data: INewAppointmentSubmissionEvent
) => {
  getEcommerceItemsByIds([data.propertyId]).then((items) => {
    // remove `index` from item[0]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { index, ...item } = items[0];
    newAppointmentSubmissionEvent({
      ...item,
      enquiry_type: "visit_appointment",
      conversion_id: generateConversionId(),
      record_id: data.recordId,
      first_name: sha256Hash(data.name),
      last_name: sha256Hash(data.name),
      hashed_email: sha256Hash(data.email),
      hashed_phone: sha256Hash(data.phone),
    });
  });
};

export const saveSubmissionToCookies = (data: {
  phone: string;
  email: string;
  name: string;
}) => {
  const cookies: {
    [key: string]: string;
  } = {
    // Reference: https://theplanttokyo.atlassian.net/browse/VHM-957
    fbq_enquireMail_ph: sha256Hash(data.phone),
    fbq_enquireMail_em: sha256Hash(data.email),
    fbq_enquireMail_fn: sha256Hash(data.name),
    fbq_enquireMail_ln: sha256Hash(data.name),
  };
  Object.keys(cookies).forEach((key) => {
    document.cookie = `${key}=${cookies[key]};path=/;expires=Fri, 31 Dec 9999 23:59:59 GMT`;
  });
};

export const saveSubmissionToSessionStorage = (
  data: {
    eventName: string;
  } & INewEnquiryFormSubmissionEvent &
    Partial<INewAppointmentSubmissionEvent>
) => {
  sessionStorage?.setItem("ga_submission_event", JSON.stringify(data));
};

export const triggerSubmissionEventFromSessionStorage = () => {
  const data = sessionStorage?.getItem("ga_submission_event");
  if (data) {
    const {
      eventName,
      propertyId,
      name,
      email,
      phone,
      recordId,
      selectedCity,
      numberOfResidents,
      budgetPerPerson,
    } = JSON.parse(data);
    if (eventName === "new_appointment_submission") {
      triggerNewAppointmentSubmissionEvent({
        propertyId,
        name,
        email,
        phone,
        recordId,
      });
    } else if (eventName === "new_enquiry_form_submission") {
      triggerNewEnquiryFormSubmissionEvent({
        propertyId,
        name,
        email,
        phone,
        recordId,
        selectedCity,
        numberOfResidents,
        budgetPerPerson,
      });
    } else if (eventName === "parking_enquiry_form_submission") {
      triggerParkingEnquiryFormSubmissionEvent({
        propertyId,
        name,
        email,
        phone,
        recordId,
      });
    }
    // save submission to cookies
    // except for corporate enquiry form
    if (!selectedCity) {
      saveSubmissionToCookies({
        phone,
        email,
        name,
      });
    }
  }
};
